import { useStrings } from "@1js/localization-components";
import type { Prompt } from "@1js/pl-card-components";
import { PromptCommand } from "@1js/pl-card-components";
import type { Group } from "@1js/pl-types";
import type { ToastIntent } from "@fluentui/react-components";
import {
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import * as React from "react";
import { useFlightContext } from "../../../hooks/useFlightContext";
import type { WebPrompt } from "../../data";
import { Actions } from "./Actions.web";
import {
  CheckAccuracyWarningLabelContent,
  CheckAccuracyWarningLabelTitle,
} from "./Detail.web.string";

type DetailsProps = {
  prompt: WebPrompt | Prompt;
  isBookmarkEnabled?: boolean;
  isBookmarked?: boolean;
  isAgentPromptSavingEnabled?: boolean;
  isPromptSavingEnabled?: boolean;
  isPromptSharingEnabled?: boolean;
  isPromptSharingViaEmailEnabled?: boolean;
  isPromptCopyLinkEnabled?: boolean;
  isPromptLikingEnabled?: boolean;
  onBookmarkToggled?: (
    prompt: Prompt,
    value?: boolean,
    toastNotify?: (
      intent: ToastIntent,
      title: string,
      body?: string,
      footer?: JSX.Element,
      headerButton?: JSX.Element
    ) => void
  ) => Promise<void>;
  onShareClicked?: (prompt: Prompt) => void;
  onCopyClicked?: (prompt: Prompt) => void;
  onDelete?: (prompt: Prompt) => void;
  onTryInAppBadgeAction?: (
    prompt: Prompt,
    product: string,
    tagRef: React.MutableRefObject<null>
  ) => void;
  isPromptSharingToWorkgroupEnabled?: boolean;
  onShareToWorkgroupClicked?: (prompts: Prompt[]) => void | Promise<void>;
  groups?: Group[];
  isPromptRemovingFromWorkgroupEnabled?: boolean;
  onRemoveFromWorkgroupClicked?: (
    prompts: Prompt[],
    groupIds: string[]
  ) => void;
  selectedGroupId?: string;
};

const useGoBoldStyles = makeStyles({
  bodyContainer: {
    maxWidth: "532px",
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    alignItems: "unset",
    alignSelf: "stretch",
  },

  promptCardContainer: {
    alignSelf: "stretch",
    justifyItems: "center",
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding("24px"),
    ...shorthands.borderRadius("6px"),

    "@media (max-width: 540px)": {
      backgroundColor: "transparent",
      ...shorthands.padding("0"),
    },
  },

  promptCard: {
    maxWidth: "254px",

    "@media (max-width: 540px)": {
      maxWidth: "100%",
    },
  },
});

const useStyles = makeStyles({
  bodyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "unset",
    alignSelf: "stretch",
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
    ...shorthands.padding("24px"),
    ...shorthands.gap("24px"),
    ...shorthands.borderRadius("12px"),
    ...shorthands.border("1px", "solid", tokens.colorTransparentStroke),

    "@media (max-width: 540px)": {
      ...shorthands.padding("16px"),
    },
  },

  promptCardContainer: {},

  promptCard: {},
});

export const Detail: React.FC<DetailsProps> = ({
  prompt,
  isBookmarked,
  isBookmarkEnabled,
  isAgentPromptSavingEnabled,
  isPromptSavingEnabled,
  isPromptSharingEnabled,
  isPromptSharingViaEmailEnabled,
  isPromptCopyLinkEnabled,
  isPromptLikingEnabled,
  onBookmarkToggled,
  onShareClicked,
  onCopyClicked,
  onDelete,
  onTryInAppBadgeAction,
  isPromptSharingToWorkgroupEnabled,
  onShareToWorkgroupClicked,
  groups,
  isPromptRemovingFromWorkgroupEnabled,
  onRemoveFromWorkgroupClicked,
  selectedGroupId,
}) => {
  const { isFlightActive } = useFlightContext();
  const isGoBoldEnabled = isFlightActive("isGoBoldPromptCardEnabled");
  const isUserPromptWarningEnabled = isFlightActive(
    "isUserPromptWarningEnabled"
  );
  const legacyStyles = useStyles();
  const goBoldStyles = useGoBoldStyles();
  const styles = isGoBoldEnabled ? goBoldStyles : legacyStyles;
  const localizations = useStrings({
    CheckAccuracyWarningLabelTitle,
    CheckAccuracyWarningLabelContent,
  });
  // We want to show a warning to user to review prompt before submitting when a user authored prompt
  // exceeds a certain length.
  const minSizeToShowUserPromptWarning = 300;
  const appearance = isFlightActive("isMosAppPromptCardV2Enabled")
    ? "web-v3"
    : "web";

  return (
    <div className={styles.bodyContainer}>
      {isUserPromptWarningEnabled &&
        prompt.DisplayText.length >= minSizeToShowUserPromptWarning &&
        prompt.Origin == "User" && (
          <MessageBar data-testid="user-content-warning" layout={"multiline"}>
            <MessageBarBody>
              <MessageBarTitle>
                {localizations.CheckAccuracyWarningLabelTitle()}
              </MessageBarTitle>
              {localizations.CheckAccuracyWarningLabelContent()}
            </MessageBarBody>
          </MessageBar>
        )}
      <div
        data-testid="detail-description"
        className={styles.promptCardContainer}
      >
        <PromptCommand command={prompt.DisplayText} appearance={appearance} />
      </div>
      <Actions
        prompt={prompt}
        isBookmarkEnabled={isBookmarkEnabled}
        isAgentPromptSavingEnabled={isAgentPromptSavingEnabled}
        isPromptSavingEnabled={isPromptSavingEnabled}
        isPromptSharingEnabled={isPromptSharingEnabled}
        isPromptSharingViaEmailEnabled={isPromptSharingViaEmailEnabled}
        isBookmarked={isBookmarked}
        isPromptCopyLinkEnabled={isPromptCopyLinkEnabled}
        isPromptLikingEnabled={isPromptLikingEnabled}
        onBookmarkToggled={onBookmarkToggled}
        onShareClicked={onShareClicked}
        onCopyClicked={onCopyClicked}
        onDelete={onDelete}
        onTryInAppBadgeAction={onTryInAppBadgeAction}
        isPromptSharingToWorkgroupEnabled={isPromptSharingToWorkgroupEnabled}
        onShareToWorkgroupClicked={onShareToWorkgroupClicked}
        groups={groups}
        isPromptRemovingFromWorkgroupEnabled={
          isPromptRemovingFromWorkgroupEnabled
        }
        onRemoveFromWorkgroupClicked={onRemoveFromWorkgroupClicked}
        selectedGroupId={selectedGroupId}
      />
    </div>
  );
};
